import { parse } from "graphql";
import { SchemaClient } from "../../../common/GraphqlClient";
import { SchemaClientKinds, Source } from "../../../common/Source";
import { EMPTY_VALUE } from "../../../Schema";
import { uniqueSortedLabels } from "./filterValues";

/** One row of the group master data — the group an equipment can belong to. */
export type GroupMaster = {
  value: string;
};

/**
 * The group master list.
 *
 * Distinct from `Groups.ts`, which reads `listIRGroupAdminMappings` to resolve reviewers for a
 * named group. This is the flat list of group names, for the `belongingToGroup` filter.
 *
 * GraphQL only; see {@link getGroupValues}.
 */
const listGroupMasters: Source<GroupMaster[]> = {
  graphql: parse(`
    query listIRGroupMasters($nextToken: String) {
      listIRGroupMasters(limit: 1000, nextToken: $nextToken) {
        nextToken
        items {
          value
        }
      }
    }
  `),
};

/**
 * Group filter options.
 *
 * Answers `[]` for any client that is not GraphQL. TODO: no DynamoDB request is declared yet.
 * Safe here because these values only populate a grid filter dropdown and never reach
 * `DataManager` — a column falls back to the app-supplied values instead. That stops holding
 * if this source is ever wired into a `data` aspect.
 *
 * @param client - Read client for the master data.
 * @returns Group names, deduplicated and sorted.
 */
export const getGroupValues = async (client: SchemaClient): Promise<string[]> => {
  if (client.kind !== SchemaClientKinds.GRAPHQL) {
    return [];
  }
  const groups = await client.queryList(listGroupMasters);
  const items = groups === EMPTY_VALUE ? [] : groups ?? [];
  return uniqueSortedLabels(items.map((group) => group.value));
};
